import React, { useState } from "react";
import { X, Bot, Loader2, Play, Search, Database, TerminalSquare, CheckCircle2, XCircle, Flag, CircleDot } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import Markdown from "../lib/Markdown";

const TOOL_ICON = { web_search: Search, data_analyze: Database, python_exec: TerminalSquare, final_answer: Flag };

export default function AgentPanel({ open, onClose, dataset }) {
  const [goal, setGoal] = useState("");
  const [useData, setUseData] = useState(!!dataset);
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const start = async () => {
    if (!goal.trim()) { toast.message("Describe a goal for the agent."); return; }
    try {
      setBusy(true); setRun(null);
      const r = await api.agentRun({ goal: goal.trim(), datasetId: useData && dataset ? dataset.datasetId : null, maxSteps: 6 });
      setRun(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Agent run failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0B0F17] flex flex-col" data-testid="agent-panel">
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-400"><Bot size={18} /></div>
          <div>
            <div className="font-head font-bold text-white">Agentic Workflow</div>
            <div className="text-[11px] text-slate-500">Real plan → tool → observe loop · tools: web search, data analysis, sandboxed python</div>
          </div>
        </div>
        <button data-testid="close-agent-panel" onClick={onClose} className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-slate-300 hover:text-white"><X size={18} /></button>
      </div>

      <div className="px-4 sm:px-6 py-4 border-b border-white/10 bg-[#0B0F17]">
        <div className="max-w-3xl mx-auto">
          <textarea data-testid="agent-goal-input" value={goal} onChange={(e) => setGoal(e.target.value)} rows={2}
            placeholder="e.g. Find the total revenue by region in my data, then compute what a 12% growth would add next year."
            className="w-full bg-[#131C2E] border border-white/10 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500/50 resize-none" />
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {dataset && (
              <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer" data-testid="agent-use-dataset">
                <input type="checkbox" checked={useData} onChange={(e) => setUseData(e.target.checked)} className="accent-blue-500" />
                Use dataset: <span className="text-slate-100">{dataset.filename}</span>
              </label>
            )}
            <div className="flex-1" />
            <button data-testid="agent-run-button" onClick={start} disabled={busy}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold text-sm disabled:opacity-50">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} {busy ? "Agent running…" : "Run agent"}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="max-w-3xl mx-auto space-y-3">
          {!run && !busy && <div className="text-sm text-slate-600 text-center py-12">Give the agent a goal. It will decide which tools to use, run them, observe results, and produce a grounded final answer.</div>}
          {busy && <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 size={16} className="animate-spin" /> The agent is reasoning and using tools…</div>}

          {run?.steps?.map((s) => {
            const Icon = TOOL_ICON[s.tool] || CircleDot;
            return (
              <div key={s.n} data-testid="agent-step" className="rounded-xl bg-[#111827] border border-white/10 p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="w-7 h-7 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400"><Icon size={14} /></span>
                  <span className="text-sm font-semibold text-white">Step {s.n} · {s.tool}</span>
                  {s.status === "done" ? <CheckCircle2 size={15} className="text-emerald-400 ml-auto" /> : <XCircle size={15} className="text-red-400 ml-auto" />}
                </div>
                {s.thought && <div className="text-[12px] text-slate-400 italic mb-2">💭 {s.thought}</div>}
                {s.input && <div className="text-[12px] text-slate-300 mb-2"><span className="text-slate-500">input:</span> {s.input}</div>}
                <pre className="text-[12px] text-slate-300 whitespace-pre-wrap bg-black/30 rounded-lg p-2.5 max-h-48 overflow-y-auto">{typeof s.observation === "string" ? s.observation : JSON.stringify(s.observation, null, 2)}</pre>
                {s.sources?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {s.sources.map((src, i) => (
                      <a key={i} href={src.uri} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-blue-300 hover:text-blue-200 truncate max-w-[200px]">{src.title || src.uri}</a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {run?.finalAnswer && (
            <div data-testid="agent-final-answer" className="rounded-xl bg-gradient-to-br from-emerald-500/10 to-blue-500/5 border border-emerald-500/30 p-5">
              <div className="text-sm font-semibold text-emerald-300 mb-2 flex items-center gap-2"><Flag size={15} /> Final answer</div>
              <div className="prose-invert text-sm text-slate-100"><Markdown>{run.finalAnswer}</Markdown></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
